// The managed department list (20260917100000_departments.sql), fetched once and shared.
//
// profiles.department_id is the source of truth. profiles.department is a text copy
// the database keeps in step until a contract migration drops it, so read a person's
// department name from this list (or a `departments(name)` embed), never from that
// column. See docs/UI-CONVENTIONS.md §2, "Fetch it once".
import { useEffect, useState } from 'react';
import { supabase } from './supabase';

export type Department = { id: string; name: string; archived: boolean };

let cache: Promise<Department[]> | null = null;

export const loadDepartments = (): Promise<Department[]> => {
  if (cache) return cache;
  const sb = supabase;
  // Promise.resolve because PostgREST's builder is a thenable, not a Promise.
  cache = !sb
    ? Promise.resolve([])
    : Promise.resolve(sb.from('departments').select('id, name, archived').order('name')).then(
        ({ data, error }) => {
          if (error) cache = null; // don't pin a failed load for the whole session
          return (data as Department[] | null) ?? [];
        },
      );
  return cache;
};

/** Call after Org Settings writes a department, or the app keeps the old list. */
export const resetDepartments = () => {
  cache = null;
};

/** Every department, archived included: a person can still sit in an archived one. */
export const useDepartments = (): Department[] => {
  const [v, setV] = useState<Department[]>([]);
  useEffect(() => {
    let live = true;
    void loadDepartments().then((d) => live && setV(d));
    return () => {
      live = false;
    };
  }, []);
  return v;
};

/** A `departments(name)` embed over profiles.department_id, as PostgREST returns it. */
export type DepartmentEmbed = { departments: { name: string } | null };

/** Flattens the embed back to the `department` name a screen already renders. */
export const withDepartmentName = <T extends DepartmentEmbed>({ departments, ...row }: T) => ({
  ...row,
  department: departments?.name ?? null,
});

export const departmentName = (
  list: readonly Department[],
  id: string | null | undefined,
): string | null => (id ? (list.find((d) => d.id === id)?.name ?? null) : null);
